// src/components/users/UserDetails.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  fetchUser,
  fetchUserActivity,
  fetchUserHierarchy,
  toggleUserStatus,
  resetUserPassword,
  verifyUser,
  clearUserError,
  clearUserSuccess,
} from '../../store/actions/userActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import UserRolesModal from './UserRolesModal';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedUser, activityLogs, hierarchy, loading, error, success } = useSelector(
    (state) => state.users
  );

  const [activeTab, setActiveTab] = useState('overview');
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showVerifyConfirm, setShowVerifyConfirm] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    
    // Header
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
      border: 'none',
      background: 'none',
      color: '#6b7280',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'all 0.2s',
    },
    backButtonHover: {
      color: '#111827',
      backgroundColor: '#f3f4f6',
    },
    backIcon: {
      width: '1.5rem',
      height: '1.5rem',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    editButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#4f46e5',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    rolesButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    rolesButtonHover: {
      backgroundColor: '#047857',
    },
    
    // Profile Header
    profileHeader: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1.5rem',
    },
    avatar: {
      flexShrink: 0,
      width: '6rem',
      height: '6rem',
      borderRadius: '9999px',
      backgroundColor: '#dbeafe',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    avatarImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    avatarFallback: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#1e40af',
    },
    profileInfo: {
      flex: 1,
    },
    profileName: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    profileTitle: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '0.75rem',
    },
    profileBadges: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginBottom: '0.75rem',
    },
    badge: {
      padding: '0.25rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    badgeDepartment: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
    badgeTeam: {
      backgroundColor: '#e0f2fe',
      color: '#0369a1',
    },
    badgeActive: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    badgeInactive: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    badgeUnverified: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    quickActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      flexShrink: 0,
    },
    quickActionButton: {
      padding: '0.375rem 0.75rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    verifyButton: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    verifyButtonHover: {
      backgroundColor: '#a7f3d0',
    },
    passwordButton: {
      backgroundColor: '#fed7aa',
      color: '#92400e',
    },
    passwordButtonHover: {
      backgroundColor: '#fdba74',
    },
    deactivateButton: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    deactivateButtonHover: {
      backgroundColor: '#fecaca',
    },
    activateButton: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    activateButtonHover: {
      backgroundColor: '#a7f3d0',
    },
    
    // Tabs
    tabsContainer: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem 0.5rem 0 0',
      padding: '0 1rem',
    },
    tab: {
      padding: '0.75rem 1.5rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
    },
    tabHover: {
      color: '#374151',
    },
    
    // Tab Content
    tabContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0 0 0.5rem 0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '1.5rem',
    },
    
    // Overview Grid
    overviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '1.5rem',
    },
    infoSection: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1rem',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      cursor: 'pointer',
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    sectionIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#6b7280',
      transition: 'transform 0.2s',
    },
    infoList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    infoLabel: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.25rem',
    },
    infoValue: {
      fontSize: '0.875rem',
      color: '#111827',
    },
    infoValueEmpty: {
      color: '#9ca3af',
      fontStyle: 'italic',
    },
    
    // Skills Section
    skillsSection: {
      gridColumn: 'span 3',
      marginTop: '1rem',
    },
    skillsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    skillTag: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      fontSize: '0.875rem',
      borderRadius: '9999px',
    },
    certificationTag: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#d1fae5',
      color: '#065f46',
      fontSize: '0.875rem',
      borderRadius: '9999px',
    },
    bioText: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.5',
      whiteSpace: 'pre-wrap',
    },
    
    // Activity Log
    timeline: {
      position: 'relative',
      paddingLeft: '2rem',
    },
    timelineItem: {
      position: 'relative',
      paddingBottom: '1.5rem',
    },
    timelineItemLast: {
      paddingBottom: 0,
    },
    timelineLine: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '2px',
      backgroundColor: '#e5e7eb',
    },
    timelineDot: {
      position: 'absolute',
      left: '-0.5rem',
      top: '0.25rem',
      width: '1rem',
      height: '1rem',
      borderRadius: '9999px',
      backgroundColor: '#9ca3af',
      border: '2px solid #ffffff',
    },
    timelineDotInfo: {
      backgroundColor: '#3b82f6',
    },
    timelineDotSuccess: {
      backgroundColor: '#10b981',
    },
    timelineDotWarning: {
      backgroundColor: '#f59e0b',
    },
    timelineDotError: {
      backgroundColor: '#ef4444',
    },
    timelineContent: {
      marginLeft: '1rem',
    },
    timelineHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.25rem',
    },
    timelineTitle: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    timelineTime: {
      fontSize: '0.75rem',
      color: '#9ca3af',
    },
    timelineDescription: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    timelineMeta: {
      fontSize: '0.75rem',
      color: '#9ca3af',
    },
    timelineMetadata: {
      marginTop: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      color: '#374151',
      fontFamily: 'monospace',
    },
    
    // Hierarchy
    hierarchyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    hierarchyCard: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1rem',
    },
    hierarchyTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    hierarchyItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.75rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.375rem',
      marginBottom: '0.5rem',
    },
    hierarchyAvatar: {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '9999px',
      backgroundColor: '#dbeafe',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    hierarchyAvatarText: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#1e40af',
    },
    hierarchyInfo: {
      flex: 1,
    },
    hierarchyName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    hierarchyDetails: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    hierarchyLink: {
      color: '#2563eb',
      textDecoration: 'none',
      fontSize: '0.875rem',
      fontWeight: 500,
      padding: '0.25rem 0.5rem',
      borderRadius: '0.375rem',
      transition: 'background-color 0.2s',
    },
    hierarchyLinkHover: {
      backgroundColor: '#eff6ff',
    },
    
    // Documents
    documentsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1rem',
    },
    documentCard: {
      padding: '1rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    documentCardHover: {
      borderColor: '#2563eb',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    documentIcon: {
      width: '2rem',
      height: '2rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    documentName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
      marginBottom: '0.25rem',
      wordBreak: 'break-word',
    },
    documentMeta: {
      fontSize: '0.75rem',
      color: '#9ca3af',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    
    // Loading Overlay
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchUser(id));
      dispatch(fetchUserActivity(id, { limit: 20 }));
      dispatch(fetchUserHierarchy(id));
    }
  }, [dispatch, id]);

  const tabs = [
    { id: 'overview', name: 'Aperçu', icon: '📊' },
    { id: 'activity', name: 'Journal d\'activité', icon: '📋' },
    { id: 'hierarchy', name: 'Hiérarchie', icon: '🔗' },
    { id: 'documents', name: 'Documents', icon: '📁' },
  ];

  const handleStatusToggle = useCallback(async () => {
    try {
      await dispatch(toggleUserStatus(id, !selectedUser.isActive));
      setShowStatusConfirm(false);
    } catch (error) {
      console.error('Status toggle failed:', error);
    }
  }, [dispatch, id, selectedUser]);

  const handlePasswordReset = useCallback(async () => {
    try {
      const result = await dispatch(resetUserPassword(id));
      setTemporaryPassword(result.temporaryPassword);
      setShowPasswordConfirm(false);
    } catch (error) {
      console.error('Password reset failed:', error);
    }
  }, [dispatch, id]);

  const handleVerify = useCallback(async () => {
    try {
      await dispatch(verifyUser(id));
      setShowVerifyConfirm(false);
    } catch (error) {
      console.error('Verification failed:', error);
    }
  }, [dispatch, id]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const getTimelineDotColor = useCallback((action) => {
    switch (action) {
      case 'CREATE':
      case 'VERIFY':
        return styles.timelineDotSuccess;
      case 'UPDATE':
      case 'PASSWORD_RESET':
        return styles.timelineDotInfo;
      case 'DEACTIVATE':
      case 'DELETE':
        return styles.timelineDotError;
      case 'ACTIVATE':
        return styles.timelineDotSuccess;
      default:
        return styles.timelineDot;
    }
  }, []);

  if (loading && !selectedUser) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div style={styles.container}>
        <Alert type="error" message="User not found" />
        <button
          onClick={() => navigate('/admin/users')}
          style={{
            ...styles.editButton,
            marginTop: '1rem',
            backgroundColor: '#2563eb',
          }}
        >
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/users')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = styles.backButtonHover.color;
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = styles.backButton.color;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg style={styles.backIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Details de l'utilisateur</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/users/${id}/edit`}
            style={styles.editButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier l'utilisateur
          </Link>
          <button
            onClick={() => setShowRolesModal(true)}
            style={styles.rolesButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.rolesButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.rolesButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Gérer les rôles
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearUserError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearUserSuccess())}
        />
      )}

      {temporaryPassword && (
        <Alert
          type="info"
          message={`Temporary Password: ${temporaryPassword}`}
          onClose={() => setTemporaryPassword(null)}
          autoClose={false}
        />
      )}

      {/* User Profile Header */}
      <div style={styles.profileHeader}>
        {/* Avatar */}
        <div style={styles.avatar}>
          {selectedUser.avatar ? (
            <img
              src={selectedUser.avatar.url}
              alt={selectedUser.displayName}
              style={styles.avatarImage}
            />
          ) : (
            <span style={styles.avatarFallback}>
              {selectedUser.firstName[0]}
              {selectedUser.lastName[0]}
            </span>
          )}
        </div>

        {/* User Info */}
        <div style={styles.profileInfo}>
          <h2 style={styles.profileName}>
            {selectedUser.displayName || `${selectedUser.firstName} ${selectedUser.lastName}`}
          </h2>
          <p style={styles.profileTitle}>{selectedUser.jobTitle || 'Aucun titre'}</p>
          
          <div style={styles.profileBadges}>
            {selectedUser.department && (
              <span style={{ ...styles.badge, ...styles.badgeDepartment }}>
                {selectedUser.department}
              </span>
            )}
            {selectedUser.team && (
              <span style={{ ...styles.badge, ...styles.badgeTeam }}>
                {selectedUser.team}
              </span>
            )}
            <span style={{
              ...styles.badge,
              ...(selectedUser.employmentStatus === 'ACTIVE' 
                ? styles.badgeActive 
                : styles.badgeInactive),
            }}>
              {selectedUser.employmentStatus}
            </span>
            {!selectedUser.isVerified && (
              <span style={{ ...styles.badge, ...styles.badgeUnverified }}>
                Non vérifié
              </span>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div style={styles.quickActions}>
          {!selectedUser.isVerified && (
            <button
              onClick={() => setShowVerifyConfirm(true)}
              style={{
                ...styles.quickActionButton,
                ...styles.verifyButton,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = styles.verifyButtonHover.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = styles.verifyButton.backgroundColor;
              }}
            >
              Vérifier l'email
            </button>
          )}
          <button
            onClick={() => setShowPasswordConfirm(true)}
            style={{
              ...styles.quickActionButton,
              ...styles.passwordButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.passwordButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.passwordButton.backgroundColor;
            }}
          >
            Réinitialiser le mot de passe
          </button>
          <button
            onClick={() => setShowStatusConfirm(true)}
            style={{
              ...styles.quickActionButton,
              ...(selectedUser.isActive ? styles.deactivateButton : styles.activateButton),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = selectedUser.isActive
                ? styles.deactivateButtonHover.backgroundColor
                : styles.activateButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = selectedUser.isActive
                ? styles.deactivateButton.backgroundColor
                : styles.activateButton.backgroundColor;
            }}
          >
            {selectedUser.isActive ? 'Désactiver' : 'Activer'} l'utilisateur
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tabHover.color;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tab.color;
              }
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={{ marginRight: '0.5rem' }}>{tab.icon}</span>
            {tab.name}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={styles.overviewGrid}>
            {/* Personal Information */}
            <div style={styles.infoSection}>
              <div
                style={styles.sectionHeader}
                onClick={() => toggleSection('personal')}
              >
                <h3 style={styles.sectionTitle}>Informations personnelles</h3>
                <svg
                  style={{
                    ...styles.sectionIcon,
                    transform: expandedSections.personal ? 'rotate(180deg)' : 'rotate(0)',
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              
              {(!expandedSections.personal || expandedSections.personal === undefined) && (
                <div style={styles.infoList}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Email</span>
                    <span style={styles.infoValue}>{selectedUser.email}</span>
                  </div>
                  {selectedUser.workEmail && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Email professionnel</span>
                      <span style={styles.infoValue}>{selectedUser.workEmail}</span>
                    </div>
                  )}
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Date de naissance</span>
                    <span style={styles.infoValue}>{formatDate(selectedUser.dateOfBirth)}</span>
                  </div>
                </div>
              )}

              {expandedSections.personal && (
                <div style={styles.infoList}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Email</span>
                    <span style={styles.infoValue}>{selectedUser.email}</span>
                  </div>
                  {selectedUser.workEmail && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Email professionnel</span>
                      <span style={styles.infoValue}>{selectedUser.workEmail}</span>
                    </div>
                  )}
                  {selectedUser.employeeId && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>ID employé</span>
                      <span style={styles.infoValue}>{selectedUser.employeeId}</span>
                    </div>
                  )}
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Date de naissance</span>
                    <span style={styles.infoValue}>{formatDate(selectedUser.dateOfBirth)}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Genre</span>
                    <span style={styles.infoValue}>{selectedUser.gender || '—'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Pronoun</span>
                    <span style={styles.infoValue}>{selectedUser.pronoun || '—'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div style={styles.infoSection}>
              <div
                style={styles.sectionHeader}
                onClick={() => toggleSection('contact')}
              >
                <h3 style={styles.sectionTitle}>Information de contact</h3>
                <svg
                  style={{
                    ...styles.sectionIcon,
                    transform: expandedSections.contact ? 'rotate(180deg)' : 'rotate(0)',
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {(!expandedSections.contact || expandedSections.contact === undefined) && (
                <div style={styles.infoList}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Téléphone</span>
                    <span style={styles.infoValue}>{selectedUser.phoneNumber || '—'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Emplacement</span>
                    <span style={styles.infoValue}>{selectedUser.officeLocation || '—'}</span>
                  </div>
                </div>
              )}

              {expandedSections.contact && (
                <div style={styles.infoList}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Numéro de téléphone</span>
                    <span style={styles.infoValue}>{selectedUser.phoneNumber || '—'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Téléphone professionnel</span>
                    <span style={styles.infoValue}>{selectedUser.workPhone || '—'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Mobile Phone</span>
                    <span style={styles.infoValue}>{selectedUser.mobilePhone || '—'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Emplacement de bureau</span>
                    <span style={styles.infoValue}>{selectedUser.officeLocation || '—'}</span>
                  </div>
                  {selectedUser.emergencyContactName && (
                    <>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Contact d'urgence</span>
                        <span style={styles.infoValue}>{selectedUser.emergencyContactName}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Téléphone d'urgence</span>
                        <span style={styles.infoValue}>{selectedUser.emergencyContactPhone}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Employment Details */}
            <div style={styles.infoSection}>
              <div
                style={styles.sectionHeader}
                onClick={() => toggleSection('employment')}
              >
                <h3 style={styles.sectionTitle}>Détails de l'emploi</h3>
                <svg
                  style={{
                    ...styles.sectionIcon,
                    transform: expandedSections.employment ? 'rotate(180deg)' : 'rotate(0)',
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {(!expandedSections.employment || expandedSections.employment === undefined) && (
                <div style={styles.infoList}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Type d'employé</span>
                    <span style={styles.infoValue}>{selectedUser.employeeType || '—'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Date d'embauche</span>
                    <span style={styles.infoValue}>{formatDate(selectedUser.hireDate)}</span>
                  </div>
                </div>
              )}

              {expandedSections.employment && (
                <div style={styles.infoList}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Type d'employé</span>
                    <span style={styles.infoValue}>{selectedUser.employeeType || '—'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Date d'embauche</span>
                    <span style={styles.infoValue}>{formatDate(selectedUser.hireDate)}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Date de début</span>
                    <span style={styles.infoValue}>{formatDate(selectedUser.startDate)}</span>
                  </div>
                  {selectedUser.probationEndDate && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Fin de probation</span>
                      <span style={styles.infoValue}>{formatDate(selectedUser.probationEndDate)}</span>
                    </div>
                  )}
                  {selectedUser.contractEndDate && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Fin du contrat</span>
                      <span style={styles.infoValue}>{formatDate(selectedUser.contractEndDate)}</span>
                    </div>
                  )}
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Fuseau horaire</span>
                    <span style={styles.infoValue}>{selectedUser.timezone}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Langue</span>
                    <span style={styles.infoValue}>{selectedUser.language}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Skills & Qualifications */}
            <div style={styles.skillsSection}>
              <div
                style={styles.sectionHeader}
                onClick={() => toggleSection('skills')}
              >
                <h3 style={styles.sectionTitle}>Compétences & Qualifications</h3>
                <svg
                  style={{
                    ...styles.sectionIcon,
                    transform: expandedSections.skills ? 'rotate(180deg)' : 'rotate(0)',
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {(!expandedSections.skills || expandedSections.skills === undefined) && (
                <div style={styles.skillsContainer}>
                  {selectedUser.skills?.slice(0, 5).map((skill) => (
                    <span key={skill} style={styles.skillTag}>
                      {skill}
                    </span>
                  ))}
                  {selectedUser.skills?.length > 5 && (
                    <span style={styles.skillTag}>
                      +{selectedUser.skills.length - 5} plus
                    </span>
                  )}
                </div>
              )}

              {expandedSections.skills && (
                <>
                  <h4 style={{ ...styles.sectionTitle, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Compétences
                  </h4>
                  <div style={styles.skillsContainer}>
                    {selectedUser.skills?.length > 0 ? (
                      selectedUser.skills.map((skill) => (
                        <span key={skill} style={styles.skillTag}>
                          {skill}
                        </span>
                      ))
                    ) : (
                      <span style={styles.infoValueEmpty}>Aucune compétence listée</span>
                    )}
                  </div>

                  <h4 style={{ ...styles.sectionTitle, fontSize: '0.875rem', margin: '1rem 0 0.5rem' }}>
                    Certifications
                  </h4>
                  <div style={styles.skillsContainer}>
                    {selectedUser.certifications?.length > 0 ? (
                      selectedUser.certifications.map((cert) => (
                        <span key={cert} style={styles.certificationTag}>
                          {cert}
                        </span>
                      ))
                    ) : (
                      <span style={styles.infoValueEmpty}>Aucune certification listée</span>
                    )}
                  </div>

                  {selectedUser.bio && (
                    <>
                      <h4 style={{ ...styles.sectionTitle, fontSize: '0.875rem', margin: '1rem 0 0.5rem' }}>
                        Bio
                      </h4>
                      <p style={styles.bioText}>{selectedUser.bio}</p>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Activity Log Tab */}
        {activeTab === 'activity' && (
          <div>
            <h3 style={{ ...styles.sectionTitle, marginBottom: '1.5rem' }}>Journal d'activité</h3>
            <div style={styles.timeline}>
              {activityLogs?.data?.map((log, index) => (
                <div
                  key={log.id}
                  style={{
                    ...styles.timelineItem,
                    ...(index === activityLogs.data.length - 1 ? styles.timelineItemLast : {}),
                  }}
                >
                  {index < activityLogs.data.length - 1 && (
                    <div style={styles.timelineLine} />
                  )}
                  <div style={{
                    ...styles.timelineDot,
                    ...getTimelineDotColor(log.action),
                  }} />
                  <div style={styles.timelineContent}>
                    <div style={styles.timelineHeader}>
                      <span style={styles.timelineTitle}>{log.action}</span>
                      <span style={styles.timelineTime}>{formatDateTime(log.createdAt)}</span>
                    </div>
                    <p style={styles.timelineDescription}>{log.description}</p>
                    {log.metadata && (
                      <pre style={styles.timelineMetadata}>
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    )}
                    <div style={styles.timelineMeta}>
                      IP: {log.ipAddress || 'N/A'} • {log.userAgent?.substring(0, 50)}...
                    </div>
                  </div>
                </div>
              ))}
              {activityLogs?.data?.length === 0 && (
                <div style={styles.emptyState}>
                  Aucun journal d'activité trouvé
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hierarchy Tab */}
        {activeTab === 'hierarchy' && hierarchy && (
          <div style={styles.hierarchyGrid}>
            {/* Manager */}
            <div style={styles.hierarchyCard}>
              <h3 style={styles.hierarchyTitle}>Manager</h3>
              {hierarchy.manager ? (
                <div style={styles.hierarchyItem}>
                  <div style={styles.hierarchyAvatar}>
                    <span style={styles.hierarchyAvatarText}>
                      {hierarchy.manager.firstName[0]}
                      {hierarchy.manager.lastName[0]}
                    </span>
                  </div>
                  <div style={styles.hierarchyInfo}>
                    <div style={styles.hierarchyName}>
                      {hierarchy.manager.firstName} {hierarchy.manager.lastName}
                    </div>
                    <div style={styles.hierarchyDetails}>
                      {hierarchy.manager.jobTitle} • {hierarchy.manager.email}
                    </div>
                  </div>
                  <Link
                    to={`/admin/users/${hierarchy.manager.id}`}
                    style={styles.hierarchyLink}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = styles.hierarchyLinkHover.backgroundColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    View
                  </Link>
                </div>
              ) : (
                <p style={styles.infoValueEmpty}>Aucun manager assigné</p>
              )}
            </div>

            {/* Direct Reports */}
            <div style={styles.hierarchyCard}>
              <h3 style={styles.hierarchyTitle}>
                Rapports directs ({hierarchy.directReports?.length || 0})
              </h3>
              <div>
                {hierarchy.directReports?.map((report) => (
                  <div key={report.id} style={styles.hierarchyItem}>
                    <div style={styles.hierarchyAvatar}>
                      <span style={styles.hierarchyAvatarText}>
                        {report.firstName[0]}
                        {report.lastName[0]}
                      </span>
                    </div>
                    <div style={styles.hierarchyInfo}>
                      <div style={styles.hierarchyName}>
                        {report.firstName} {report.lastName}
                      </div>
                      <div style={styles.hierarchyDetails}>
                        {report.jobTitle} • {report.department}
                      </div>
                    </div>
                    <Link
                      to={`/admin/users/${report.id}`}
                      style={styles.hierarchyLink}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = styles.hierarchyLinkHover.backgroundColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Voir
                    </Link>
                  </div>
                ))}
                {hierarchy.directReports?.length === 0 && (
                  <p style={styles.infoValueEmpty}>Aucun rapport direct</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            <h3 style={{ ...styles.sectionTitle, marginBottom: '1.5rem' }}>Documents</h3>
            {selectedUser.documents?.length > 0 ? (
              <div style={styles.documentsGrid}>
                {selectedUser.documents.map((doc) => (
                  <div
                    key={doc.id}
                    style={styles.documentCard}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = styles.documentCardHover.borderColor;
                      e.currentTarget.style.boxShadow = styles.documentCardHover.boxShadow;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => window.open(doc.url, '_blank')}
                  >
                    <svg style={styles.documentIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div style={styles.documentName}>{doc.name}</div>
                    <div style={styles.documentMeta}>
                      {formatFileSize(doc.size)} • {new Date(doc.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                Aucun document trouvé pour cet utilisateur
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <UserRolesModal
        isOpen={showRolesModal}
        onClose={() => setShowRolesModal(false)}
        user={selectedUser}
      />

      <ConfirmDialog
        isOpen={showStatusConfirm}
        onClose={() => setShowStatusConfirm(false)}
        onConfirm={handleStatusToggle}
        title={`${selectedUser?.isActive ? 'Désactiver' : 'Activer'} Utilisateur`}
        message={`Etes-vous sûr de vouloir ${selectedUser?.isActive ? 'désactiver' : 'activer'} ${selectedUser?.displayName || selectedUser?.firstName}?`}
        confirmText={selectedUser?.isActive ? 'Désactiver' : 'Activer'}
        cancelText="Annuler"
        type={selectedUser?.isActive ? 'warning' : 'success'}
      />

      <ConfirmDialog
        isOpen={showPasswordConfirm}
        onClose={() => setShowPasswordConfirm(false)}
        onConfirm={handlePasswordReset}
        title="Réinitialiser le mot de passe"
        message={`Etes-vous sûr de vouloir réinitialiser le mot de passe pour ${selectedUser?.displayName || selectedUser?.firstName}? Un mot de passe temporaire sera généré.`}
        confirmText="Réinitialiser"
        cancelText="Annuler"
        type="warning"
      />

      <ConfirmDialog
        isOpen={showVerifyConfirm}
        onClose={() => setShowVerifyConfirm(false)}
        onConfirm={handleVerify}
        title="Vérifier l'utilisateur"
        message={`Etes-vous sûr de vouloir vérifier ${selectedUser?.displayName || selectedUser?.firstName}?`}
        confirmText="Vérifier"
        cancelText="Annuler"
        type="info"
      />
    </div>
  );
};

export default UserDetails;